'use client';

import { Button } from '@/components/ui/button';
import { APP_ROUTES } from '@/config/routes';
import '@/styles/user-store-setup.css';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { useErrorTracking } from '@/hooks';

const NotFoundPage = () => {
  const pathname = usePathname();
  const { captureError } = useErrorTracking();
  // Effects run twice under dev StrictMode — key on the path so a 404 is
  // reported once per URL rather than once per mount.
  const reportedPath = useRef<string | null>(null);

  useEffect(() => {
    if (reportedPath.current === pathname) return;
    reportedPath.current = pathname;

    // Every notFound() in the app renders this page, so this one spot sees them
    // all — a missing org, a stale conversation link, an unmatched route —
    // without any call site needing to know about tracking.
    //
    // The message is deliberately constant: putting the path in it would mint a
    // new PostHog issue per org/conversation id. The URL rides along as a
    // filterable property instead.
    const error = new Error('404 page not found');
    error.name = 'NotFoundError';

    captureError(error, {
      error_source: 'not_found_page',
      error_code: '404',
      path: pathname
    });
  }, [pathname, captureError]);

  return (
    <div className="flex flex-col justify-center items-center gap-6 h-screen w-screen user-store-setup">
      <div className="text-slate-900 drop-shadow-[6px_0px_0px_#542cc2] sm:drop-shadow-[10px_0px_0px_#542cc2] text-[100px] sm:text-[200px] md:text-[300px] font-bold leading-[80px] sm:leading-[160px] md:leading-[240px]">
        404
      </div>
      <div className="flex flex-col items-center gap-2 text-gray-950">
        <div className="text-2xl sm:text-4xl font-bold leading-6 sm:leading-10">
          Oops! Page not found
        </div>
        <div className="w-[360px] sm:w-[454px] text-center text-sm sm:text-lg font-normal leading-5 sm:leading-7">
          {`The page you're looking for doesn’t exist. Please check the URL or return to the previous
          page`}
        </div>
      </div>
      <Link href={APP_ROUTES.USER_DASHBOARD} passHref prefetch>
        <Button className="h-10 px-4 py-3 rounded-lg text-sm font-medium leading-tight">
          Go to dashboard
        </Button>
      </Link>
    </div>
  );
};

export default NotFoundPage;
